"use client";

import { skipToken, useQuery } from "@apollo/client/react";
import type { PaginationState } from "@tanstack/react-table";
import { useParams, useRouter } from "next/navigation";
import { use, useEffect, useMemo, useState } from "react";

import TrendingCoinsTable from "@/components/app/trade/trending-coins/trending-coins-table";
import CopyToClipboard from "@/components/shared/copy-to-clipboard";
import { EmptyState } from "@/components/shared/empty-state";
import { useIsLoggedIn } from "@/components/shared/is-logged-in";
import TablePagination from "@/components/shared/table-pagination";
import UserSelect from "@/components/shared/users-search/user-select";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useFiltersAndSort } from "@/contexts/filters-sort-context";
import { UserContext } from "@/contexts/user-context";
import type {
  TradingTopTradersByTokenByVolume,
  TradingTopTradersByTokenByVolumeFilter,
} from "@/graphql/codegen/graphql";
import {
  TradingTopTradersByTokenByVolumesDocument,
  TradingTopTradersByTokenByVolumesOrderBy,
} from "@/graphql/codegen/graphql";
import { useResolveUsername } from "@/hooks/use-resolve-username";
import { APP_ROUTES } from "@/utils/constants";
import { shortenLongWord } from "@/utils/helpers";
import { getOrderByKey } from "@/utils/trade";
import type { PartialAccount } from "@/utils/types";

import { formatFilters } from "../utils";
import {
  COLUMNS_WIDTH,
  PortfolioTimeRange,
  TimeRangeLabels,
} from "./portfolio-constants";
import { PortfolioFilterPopover } from "./portfolio-filter-popover";
import { columns as tableColumns } from "./portfolio-table-columns";

export const INITIAL_FILTERS_AND_SORTS = {
  initialFilters: [],
  initialSort: null,
};

type SortingState = {
  id: TradingTopTradersByTokenByVolumesOrderBy;
  desc: boolean;
};

export const DEFAULT_PORTFOLIO_USER = "nader";

const PortfolioTableWrapper = () => {
  const { username } = useParams<{ username: string }>();
  const { currentUserPublicKey } = use(UserContext);

  const {
    username: resolvedUsername,
    publicKey: resolvedPublicKey,
    loading: resolvingUser,
  } = useResolveUsername(username || currentUserPublicKey);
  const router = useRouter();
  const isLoggedIn = useIsLoggedIn();

  const [selectedUser, setSelectedUser] = useState<PartialAccount | null>(null);
  const [redirecting, setRedirecting] = useState(false);
  const [activeTimeRangeTab, setActiveTimeRangeTab] =
    useState<PortfolioTimeRange>(PortfolioTimeRange.ALL_TIME);
  const [pageOffset, setPageOffset] = useState(0);
  const [itemsPerPage] = useState(10);
  const [columnSorting, setColumnSorting] = useState<SortingState[]>([
    {
      id: TradingTopTradersByTokenByVolumesOrderBy.TotalVolumeUsdDesc,
      desc: true,
    },
  ]);

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: itemsPerPage,
  });
  const {
    setFilters,
    findFilterByName,
    removeFilter,
    filters,
    isFilterActive,
    updateFilter,
    syncLastSavedFilters,
    setFiltersToLastSaved,
  } = useFiltersAndSort(INITIAL_FILTERS_AND_SORTS);

  const methods = {
    findFilterByName,
    updateFilter,
    removeFilter,
    isFilterActive,
    syncLastSavedFilters,
    setFiltersToLastSaved,
  };

  const columns = useMemo(() => tableColumns(), []);

  const tradeQueryVariables = useMemo(() => {
    if (!resolvedPublicKey) {
      return null;
    }

    const formatFiltersParams =
      Object.keys(filters).length > 0 ? formatFilters(filters) : {};

    const filter: TradingTopTradersByTokenByVolumeFilter = {
      ...formatFiltersParams,
      timeWindow: {
        equalTo: activeTimeRangeTab,
      },
      traderPublicKey: {
        equalTo: resolvedPublicKey,
      },
      tokenPublicKey: {
        notEqualTo: "*",
      },
    };

    return {
      first: itemsPerPage,
      offset: pageOffset,
      orderBy: getOrderByKey(
        columnSorting,
        TradingTopTradersByTokenByVolumesOrderBy,
        TradingTopTradersByTokenByVolumesOrderBy.TotalVolumeUsdDesc,
      ),
      filter,
    };
  }, [
    activeTimeRangeTab,
    columnSorting,
    filters,
    itemsPerPage,
    pageOffset,
    resolvedPublicKey,
  ]);

  const {
    data: tradesData,
    loading: loadingTrades,
    refetch: refetchTrades,
  } = useQuery(
    TradingTopTradersByTokenByVolumesDocument,
    tradeQueryVariables
      ? {
          fetchPolicy: "network-only",
          notifyOnNetworkStatusChange: true,
          variables: tradeQueryVariables,
        }
      : skipToken,
  );

  const trades =
    (tradesData?.tradingTopTradersByTokenByVolumes?.nodes?.filter(
      Boolean,
    ) as TradingTopTradersByTokenByVolume[]) ?? [];
  const totalCount =
    tradesData?.tradingTopTradersByTokenByVolumes?.totalCount ?? 0;
  const isFirstLoad = resolvingUser || (loadingTrades && !tradesData);

  const resetPagination = () => {
    setPagination({
      pageIndex: 0,
      pageSize: itemsPerPage,
    });
    setPageOffset(0);
  };

  const handleTimeRangeTab = (tab: PortfolioTimeRange) => {
    setActiveTimeRangeTab(tab);
    resetPagination();
  };

  const handleResetFilters = () => {
    setActiveTimeRangeTab(PortfolioTimeRange.ALL_TIME);
    setFilters(INITIAL_FILTERS_AND_SORTS.initialFilters, true);
    resetPagination();
  };

  const handleApplyFilters = () => {
    resetPagination();
  };

  const handleSelectedUsers = (user: PartialAccount | null) => {
    if (!user) {
      setSelectedUser(null);
      return;
    }

    setSelectedUser(user);
    setRedirecting(true);
    router.push(
      `${APP_ROUTES.tradePortfolio}/${user.username || user.publicKey}`,
    );
  };

  const handleColumnSortingChange = (sorting: SortingState[]) => {
    setColumnSorting(sorting);
    resetPagination();
  };

  const onPaginate = (nextPageIndex: number) => {
    const offset = nextPageIndex * itemsPerPage;

    setPagination({
      pageIndex: nextPageIndex,
      pageSize: itemsPerPage,
    });
    setPageOffset(offset);
  };

  const refetchWithUpdatedParams = async () => {
    await refetchTrades();
  };

  useEffect(() => {
    if (!isLoggedIn && !username) {
      router.replace(APP_ROUTES.tradePortfolio + `/${DEFAULT_PORTFOLIO_USER}`);
    }
  }, [isLoggedIn, router, username]);

  const loading = loadingTrades || redirecting;

  if (!isLoggedIn && !username) {
    return null;
  }

  return isFirstLoad ? (
    <div className="flex flex-col">
      <div
        className="
          flex w-full flex-col items-start justify-normal gap-2
          xl:flex-row xl:items-end xl:justify-between
        "
      >
        <Skeleton
          className="
            mb-4 h-[38px] w-full
            xl:mb-0 xl:w-[250px]
          "
        />
        <Skeleton
          className="
            h-[38px] w-full
            xl:w-[535px]
          "
        />
      </div>
      <Skeleton className="mt-4 h-[620px] w-full" />
    </div>
  ) : (
    <div className="min-h-[700px]">
      <div
        className="
          mb-4 flex flex-col items-start justify-between gap-2
          xl:flex-row xl:items-center
        "
      >
        <div
          className="
            mt-2 mb-4 flex w-full flex-col
            lg:block lg:w-auto
            xl:my-0
          "
        >
          <h2
            className="
              flex font-sans text-xl font-semibold text-muted-foreground
            "
          >
            {!username && <span>My Portfolio</span>}
            {username && resolvedPublicKey && (
              <>
                {resolvedUsername ? (
                  <span className="capitalize">
                    {resolvedUsername}'s Portfolio
                  </span>
                ) : (
                  <span className="flex items-center gap-1">
                    <CopyToClipboard
                      text={`${resolvedPublicKey} Portfolio`}
                      className="cursor-auto text-xl"
                      showIcon={false}
                    >
                      {shortenLongWord(resolvedPublicKey) || ""}
                    </CopyToClipboard>
                  </span>
                )}
              </>
            )}
          </h2>
          <p className="text-muted">
            Token holdings for {resolvedUsername || resolvedPublicKey}
          </p>
        </div>

        <div
          className="
            flex w-full flex-col justify-between gap-6
            sm:flex-row sm:items-center sm:gap-2
            md:w-auto md:justify-normal
          "
        >
          {resolvedPublicKey && (
            <div
              className="
                w-full overflow-x-auto
                md:w-auto
              "
            >
              <div className="flex min-w-[315px] gap-x-1.5">
                {Object.values(PortfolioTimeRange).map((tab) => (
                  <Button
                    key={tab}
                    onClick={() => handleTimeRangeTab(tab)}
                    variant={
                      activeTimeRangeTab === tab ? "outlinePrimary" : "outline"
                    }
                    disabled={loadingTrades}
                    size="lg"
                    className="px-4 py-2"
                  >
                    {TimeRangeLabels[tab]}
                  </Button>
                ))}
              </div>
            </div>
          )}

          <UserSelect
            selectedAccount={selectedUser}
            popoverClassName="w-[220px]"
            onSelect={handleSelectedUsers}
            placeholder="Search any user..."
            skipValidation
            className="
              hover-visible:outline-none
              relative z-1 flex h-11 w-[220px] rounded-full border border-border
              bg-input px-3 py-2 text-sm text-muted
              hover:border-foreground hover:bg-input hover:text-muted
              focus:border-foreground focus:bg-input
              focus-visible:outline-hidden
              disabled:cursor-not-allowed disabled:opacity-50
            "
          />
        </div>
      </div>

      {resolvedPublicKey ? (
        <>
          <PortfolioFilterPopover
            methods={methods}
            filters={filters}
            onResetFilters={handleResetFilters}
            onApplyFilters={handleApplyFilters}
          />

          <TrendingCoinsTable<TradingTopTradersByTokenByVolume>
            loading={loading}
            columns={columns}
            data={trades}
            columnSorting={columnSorting}
            setColumnSorting={(sorting) =>
              handleColumnSortingChange(sorting as SortingState[])
            }
            wrapperClassName={!trades.length ? "min-w-full" : "min-w-[1024px]"}
            pagination={pagination}
            pageOffset={pageOffset}
            columnsWidth={COLUMNS_WIDTH}
            refetchData={refetchWithUpdatedParams}
            noItemsMessage="No trades found."
          />

          <TablePagination
            loading={loading}
            pagination={pagination}
            onPaginate={onPaginate}
            itemsPerPage={itemsPerPage}
            totalCount={totalCount}
          />
        </>
      ) : (
        <EmptyState
          image="/assets/empty-20.png"
          classNames="mt-[80px]"
          title="No users found"
          description="No users found with the provided username or public key"
        />
      )}
    </div>
  );
};

export default PortfolioTableWrapper;
